import { MapPin, Calendar } from 'lucide-react';
import { cvData } from '../../data/cv';
import { useInView } from '../../hooks/useInView';

const EduCard = ({ edu, idx }: { edu: typeof cvData.education[0]; idx: number }) => {
  const { ref, inView } = useInView();
  return (
    <div
      ref={ref}
      className={`group bg-card rounded-2xl p-6 sm:p-7 md:p-8 border border-border border-b-4 border-b-accent shadow-card hover:shadow-elevated hover:-translate-y-1.5 transition-all duration-500 relative overflow-hidden h-full ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
      style={{ transitionDelay: `${idx * 100}ms` }}
    >
      <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-accent mb-3 block">
        Academic Credential
      </span>
      <h3
        className="text-xl md:text-2xl font-bold text-foreground leading-snug mb-3"
        style={{ fontFamily: "'Playfair Display', serif" }}
      >
        {edu.degree}
      </h3>
      <p className="text-sm text-primary font-semibold mb-4">{edu.institution}</p>
      <div className="flex flex-wrap items-center gap-4 text-[11px] uppercase tracking-wider text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <MapPin size={11} />
          {edu.location}
        </span>
        <span className="flex items-center gap-1.5">
          <Calendar size={11} />
          {edu.year}
        </span>
      </div>
    </div>
  );
};

export const Education = () => {
  const { ref: headRef, inView: headIn } = useInView();

  return (
    <section id="education" className="py-20 sm:py-24 md:py-28 px-4 sm:px-6 md:px-12 lg:px-20 bg-background relative">
      <div className="max-w-7xl mx-auto">

        <div ref={headRef} className={`flex flex-col sm:flex-row sm:items-end gap-4 sm:gap-6 mb-10 sm:mb-12 md:mb-16 transition-all duration-700 ${headIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="shrink-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-accent mb-3">Academic</p>
            <h2
              className="text-3xl sm:text-4xl md:text-4xl lg:text-5xl font-bold text-foreground whitespace-nowrap"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Education
            </h2>
          </div>
          <div className="hidden sm:block h-px w-full bg-border mb-3" />
        </div>

        <div className="grid sm:grid-cols-2 gap-6 md:gap-8">
          {cvData.education.map((edu, idx) => (
            <EduCard key={idx} edu={edu} idx={idx} />
          ))}
        </div>

        {cvData.researchAffiliations && cvData.researchAffiliations.length > 0 && (
          <div className="mt-16 sm:mt-20">
            <div className="flex flex-col sm:flex-row sm:items-end gap-4 sm:gap-6 mb-8 sm:mb-10">
              <div className="shrink-0">
                <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-accent mb-3">Research</p>
                <h3
                  className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Research &amp; Affiliations
                </h3>
              </div>
              <div className="hidden sm:block h-px w-full bg-border mb-2" />
            </div>

            <div className="grid sm:grid-cols-2 gap-6 md:gap-8">
              {cvData.researchAffiliations.map((item, idx) => (
                <div
                  key={idx}
                  className="group bg-card rounded-2xl p-6 sm:p-7 md:p-8 border border-border border-b-4 border-b-accent shadow-card hover:shadow-elevated hover:-translate-y-1.5 transition-all duration-500 relative overflow-hidden h-full"
                >
                  <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-accent mb-3 block">
                    {item.tag} · Research Affiliation
                  </span>
                  <h4
                    className="text-xl md:text-2xl font-bold text-foreground leading-snug mb-3"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {item.title}
                  </h4>
                  <p className="text-sm text-primary font-semibold mb-3">{item.institution}</p>
                  <p className="text-sm text-foreground/70 leading-relaxed mb-4">{item.blurb}</p>
                  <div className="flex flex-wrap items-center gap-4 text-[11px] uppercase tracking-wider text-muted-foreground">
                    <span className="flex items-center gap-1.5">
                      <MapPin size={11} />
                      {item.location}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Calendar size={11} />
                      {item.year}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
